<template>
  <div>
    <v-card :flat="embedded">
      <v-card-title v-if="!embedded" class="headline primary">
        <span class="white--text">Correct NBN Order</span>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert
          v-if="!embedded"
          dense
          class="mb-4"
          color="orange"
          icon="mdi-alert"
        >
          Only correct an order here when a request for manual modification has
          been raised and the order has already been modified in the NBN Co portal.
        </v-alert>
        <v-row v-if="order" dense class="mb-5">
          <v-col cols="12" md="3">
            <div class="subtitle-1 font-weight-bold">NBN Order ID</div>
            <div class="title">{{ displayValue(currentValues.NbnOrderId) }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="subtitle-1 font-weight-bold">Location ID</div>
            <div class="title">{{ displayValue(currentValues.LocationId) }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="subtitle-1 font-weight-bold">Product</div>
            <div class="title">{{ displayValue(currentValues.Product) }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="subtitle-1 font-weight-bold">Request Type</div>
            <div class="title">{{ displayValue(currentValues.RequestType) }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="subtitle-1 font-weight-bold">Customer Reference</div>
            <div class="title">{{ displayValue(currentValues.CustomerReference) }}</div>
          </v-col>
          <v-col v-if="nbnAssureDisplay" cols="12" md="3">
            <div class="subtitle-1 font-weight-bold">NBN Assure</div>
            <div class="title">{{ nbnAssureDisplay }}</div>
          </v-col>
        </v-row>

        <v-simple-table dense class="correction-table mb-4">
          <thead>
            <tr>
              <th>Field</th>
              <th>Current Value</th>
              <th>New Value</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="field in editableFields"
              :key="field.key"
              :class="{ 'changed-row': isFieldChanged(field.key) }"
            >
              <td class="font-weight-medium">{{ field.label }}</td>
              <td class="current-value">{{ displayFieldValue(field, currentValues[field.key]) }}</td>
              <td>
                <v-text-field
                  v-if="field.readOnly || field.disabled"
                  :value="draftValues[field.key]"
                  dense
                  outlined
                  hide-details
                  disabled
                  readonly
                  class="mt-2 mb-2"
                ></v-text-field>
                <v-autocomplete
                  v-else-if="field.input === 'wholesaler'"
                  v-model="selectedWholesalerAccount"
                  :items="wholesalerOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  class="mt-2 mb-2"
                ></v-autocomplete>
                <v-select
                  v-else-if="field.input === 'bandwidth'"
                  v-model="draftValues[field.key]"
                  :items="bandwidthOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  class="mt-2 mb-2"
                ></v-select>
                <v-combobox
                  v-else-if="field.input === 'status'"
                  v-model="draftValues[field.key]"
                  :items="statusOptions"
                  dense
                  outlined
                  hide-details
                  class="mt-2 mb-2"
                ></v-combobox>
                <v-text-field
                  v-else
                  v-model="draftValues[field.key]"
                  dense
                  outlined
                  hide-details
                  class="mt-2 mb-2"
                ></v-text-field>
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-textarea
          ref="reasonField"
          v-model="reason"
          label="Reason / comment"
          hint="Include the Jira ticket number so this correction can be traced to the original request."
          persistent-hint
          :rules="shouldValidateReason ? reasonRules : []"
          outlined
          dense
          rows="2"
          auto-grow
          required
          @blur="shouldValidateReason = true"
        ></v-textarea>

        <div class="d-flex justify-end mb-6">
          <v-btn v-if="!embedded" class="mr-3" @click="closeForm">Cancel</v-btn>
          <v-btn
            class="primary"
            :disabled="!canReviewChanges"
            @click="openConfirm"
          >
            Review changes
          </v-btn>
        </div>

        <v-divider class="mb-4"></v-divider>
        <div class="subtitle-1 font-weight-bold mb-2">Audit history</div>
        <v-simple-table v-if="flattenedAuditEntries.length" dense>
          <thead>
            <tr>
              <th>Field</th>
              <th>Old Value</th>
              <th>New Value</th>
              <th>Modified By</th>
              <th>Modified Date</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, index) in flattenedAuditEntries" :key="index">
              <td>{{ entry.field }}</td>
              <td>{{ displayValue(entry.oldValue) }}</td>
              <td>{{ displayValue(entry.newValue) }}</td>
              <td>{{ entry.userEmail }}</td>
              <td>{{ formatAuditDate(entry.dateTime) }}</td>
              <td>{{ entry.reason || "—" }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <div v-else class="grey--text">No audit history found for this order.</div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirmDialog" persistent max-width="800">
      <v-card>
        <v-card-title class="headline">Confirm NBN order correction</v-card-title>
        <v-card-text>
          <p>The following values will be updated. This change will be audited.</p>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Field</th>
                <th>Current Value</th>
                <th>New Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="change in pendingChanges" :key="change.field">
                <td>{{ change.label }}</td>
                <td>{{ displayChangeValue(change, change.oldValue) }}</td>
                <td>{{ displayChangeValue(change, change.newValue) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="mt-4 mb-0">
            <strong>Reason:</strong> {{ reason }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="confirmDialog = false">Back</v-btn>
          <v-btn class="primary" :disabled="isSubmitting" @click="applyCorrection">
            Apply correction
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  NBN_ORDER_CORRECTION_FIELDS,
  NBN_ORDER_CORRECTION_STATUSES
} from "@/constants/nbn-order-correction-constants";
import {
  applyCorrectionValues,
  applyWholesalerSelection,
  buildCorrectionAuditLog,
  buildCorrectionDraft,
  buildCorrectNbnOrderPayload,
  filterAuditLogsForOrder,
  flattenAuditEntries,
  getWholesalerDisplayName,
  getCorrectionBandwidthLabel,
  getCorrectionBandwidthProfiles,
  getCorrectionChanges,
  getNbnAssureDisplayValue,
  isCorrectionReasonProvided,
  isNbnAssureSelected
} from "@/utils/nbn-order-correction";
import { formatDateTime } from "@/plugins/global-functions";

export default {
  name: "NbnOrderCorrectionForm",
  props: {
    order: {
      type: Object,
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    fields: NBN_ORDER_CORRECTION_FIELDS,
    statusOptions: NBN_ORDER_CORRECTION_STATUSES,
    currentValues: {},
    draftValues: {},
    reason: "",
    confirmDialog: false,
    isSubmitting: false,
    shouldValidateReason: false,
    localAuditLogs: []
  }),
  computed: {
    editableFields() {
      return this.fields.filter(field => !field.readOnly);
    },
    pendingChanges() {
      return getCorrectionChanges(this.currentValues, this.draftValues);
    },
    hasChanges() {
      return this.pendingChanges.length > 0;
    },
    hasReason() {
      return isCorrectionReasonProvided(this.reason);
    },
    canReviewChanges() {
      return this.hasChanges && this.hasReason && this.order && this.order.id;
    },
    reasonRules() {
      return [
        value =>
          isCorrectionReasonProvided(value) ||
          "Reason is required and must include the Jira ticket number."
      ];
    },
    bandwidthOptions() {
      const options = getCorrectionBandwidthProfiles(
        this.currentValues.Product,
        this.featureFlags && this.featureFlags.sog6028,
        isNbnAssureSelected(this.order, this.nbnOrder)
      ).map(item => ({
        text: item.text,
        value: item.value
      }));
      const current = this.currentValues.AvcdBandwidthProfile;
      if (current && !options.some(item => item.value === current)) {
        options.unshift({ text: current, value: current });
      }
      return options;
    },
    orderAuditLogs() {
      return filterAuditLogsForOrder(
        this.localAuditLogs.concat(this.auditLogs || []),
        this.order
      );
    },
    flattenedAuditEntries() {
      return flattenAuditEntries(this.orderAuditLogs);
    },
    nbnAssureDisplay() {
      return getNbnAssureDisplayValue(this.order, this.nbnOrder);
    },
    wholesalerOptions() {
      const source = Array.isArray(this.wholesalers)
        ? this.wholesalers
        : (this.wholesalers && this.wholesalers.data) || [];
      const list = source.map(item => ({
        text:
          getWholesalerDisplayName(item) ||
          String(item.accountNumber == null ? "" : item.accountNumber),
        value: item.accountNumber == null ? "" : String(item.accountNumber)
      }));
      const currentAccount = this.draftValues.WholesalerAccountNumber;
      if (
        currentAccount &&
        !list.some(item => item.value === String(currentAccount))
      ) {
        list.unshift({
          text: this.draftValues.Wholesaler || String(currentAccount),
          value: String(currentAccount)
        });
      }
      return list;
    },
    selectedWholesalerAccount: {
      get() {
        return this.draftValues.WholesalerAccountNumber || "";
      },
      set(accountNumber) {
        const next = applyWholesalerSelection(this.wholesalers, accountNumber);
        this.$set(this.draftValues, "Wholesaler", next.Wholesaler);
        this.$set(
          this.draftValues,
          "WholesalerAccountNumber",
          next.WholesalerAccountNumber
        );
      }
    }
  },
  created() {
    this.getWholesalers().catch(() => {});
  },
  watch: {
    order: {
      immediate: true,
      handler(nextOrder, previousOrder) {
        if (
          !previousOrder ||
          !nextOrder ||
          String(previousOrder.id) !== String(nextOrder.id)
        ) {
          this.localAuditLogs = [];
        }
        this.resetDraft();
        this.loadAuditHistory();
      }
    }
  },
  methods: {
    resetDraft() {
      const values = buildCorrectionDraft(this.order);
      this.currentValues = Object.assign({}, values);
      this.draftValues = Object.assign({}, values);
      this.reason = "";
      this.confirmDialog = false;
      this.shouldValidateReason = false;
      this.resetReasonValidation();
    },
    resetReasonValidation() {
      this.$nextTick(() => {
        const field = this.$refs.reasonField;
        if (field && field.resetValidation) {
          field.resetValidation();
        }
      });
    },
    displayValue(value) {
      return value === undefined || value === null || value === "" ? "—" : value;
    },
    displayBandwidthValue(value) {
      return this.displayValue(
        getCorrectionBandwidthLabel(
          value,
          this.currentValues.Product,
          this.featureFlags && this.featureFlags.sog6028
        )
      );
    },
    displayFieldValue(field, value) {
      if (field && field.input === "bandwidth") {
        return this.displayBandwidthValue(value);
      }

      return this.displayValue(value);
    },
    displayChangeValue(change, value) {
      if (change && change.field === "AvcdBandwidthProfile") {
        return this.displayBandwidthValue(value);
      }

      return this.displayValue(value);
    },
    isFieldChanged(fieldKey) {
      return this.pendingChanges.some(change => change.field === fieldKey);
    },
    formatAuditDate(value) {
      return formatDateTime(value, true) || "—";
    },
    openConfirm() {
      this.shouldValidateReason = true;
      if (!this.canReviewChanges) {
        return;
      }
      this.confirmDialog = true;
    },
    closeForm() {
      this.confirmDialog = false;
      this.$emit("close");
    },
    loadAuditHistory() {
      return this.retrieveAuditLogs()
        .then(() => {
          this.pruneResolvedLocalAuditLogs();
        })
        .catch(() => {});
    },
    pruneResolvedLocalAuditLogs() {
      const remoteLogs = filterAuditLogsForOrder(this.auditLogs, this.order);
      this.localAuditLogs = this.localAuditLogs.filter(localLog => {
        const localPayload = localLog && localLog.requestPayload;
        return !remoteLogs.some(remoteLog => {
          return (
            remoteLog.requestType === localLog.requestType &&
            remoteLog.requestPayload === localPayload
          );
        });
      });
    },
    applyCorrection() {
      const vm = this;
      if (!vm.order || !vm.order.id || !vm.hasChanges || !vm.hasReason) {
        return;
      }

      const payload = buildCorrectNbnOrderPayload(
        vm.order,
        vm.currentValues,
        vm.draftValues,
        vm.reason
      );

      vm.isSubmitting = true;
      vm.correctNbnOrderAsync(payload)
        .then(() => {
          const updatedOrder = applyCorrectionValues(vm.order, payload.newValues);
          const userEmail = (vm.user && vm.user.email) || "";

          vm.localAuditLogs = [
            buildCorrectionAuditLog(vm.order, payload, userEmail)
          ].concat(vm.localAuditLogs);
          vm.currentValues = Object.assign({}, vm.draftValues);
          vm.reason = "";
          vm.shouldValidateReason = false;
          vm.confirmDialog = false;
          vm.resetReasonValidation();
          vm.$emit("corrected", updatedOrder);

          return vm.loadAuditHistory();
        })
        .catch(() => {})
        .finally(() => {
          vm.isSubmitting = false;
        });
    }
  }
};
</script>

<style scoped>
.correction-table td,
.correction-table th {
  vertical-align: middle;
}
.current-value {
  max-width: 280px;
  word-break: break-word;
}
.changed-row {
  background-color: #fff8e1;
}
</style>
